/**
 * ChatWindow Component - Main chat interface for messaging
 */

import { useState, useEffect, useRef } from 'react';
import {
  sendMessage,
  subscribeToMessages,
  markConversationAsRead,
  setTypingStatus,
  subscribeToTyping
} from '../services/chatService';
import MessageBubble from './MessageBubble';
import '../styles/ChatWindow.css';

// Helper function to safely get a string value (prevents rendering objects)
const safeString = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
};

const ChatWindow = ({ currentUser, otherUser, roomCode = null, isMinimized = false, onToggleMinimize, onClose, isFloating = false }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const hasMarkedReadRef = useRef(false);

  // Auto-focus input when chat opens
  useEffect(() => {
    inputRef.current?.focus();
  }, [otherUser.userId]);

  // Subscribe to messages
  useEffect(() => {
    if (!currentUser || !otherUser) return;

    try {
      const unsubscribe = subscribeToMessages(
        currentUser.uid,
        otherUser.userId,
        (updatedMessages) => {
          try {
            // Ensure updatedMessages is an array
            const validMessages = Array.isArray(updatedMessages) ? updatedMessages : [];
            setMessages(validMessages);

            // Only mark as read if there are unread messages TO the current user
            // and we haven't marked them in the last second (debounce)
            if (!hasMarkedReadRef.current) {
              const hasUnreadMessages = validMessages.some(
                msg => msg.to === currentUser.uid && !msg.read
              );

              if (hasUnreadMessages) {
                hasMarkedReadRef.current = true;
                markConversationAsRead(currentUser.uid, otherUser.userId).catch((err) => {
                  console.error('Error marking conversation as read:', err);
                });

                // Reset the flag after 1 second to allow future marking
                setTimeout(() => {
                  hasMarkedReadRef.current = false;
                }, 1000);
              }
            }
          } catch (error) {
            console.error('Error processing messages update:', error);
          }
        }
      );

      return () => {
        try {
          unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing from messages:', error);
        }
      };
    } catch (error) {
      console.error('Error setting up message subscription:', error);
    }
  }, [currentUser, otherUser]);

  // Subscribe to typing indicator
  useEffect(() => {
    if (!currentUser || !otherUser) return;

    try {
      const unsubscribe = subscribeToTyping(
        currentUser.uid,
        otherUser.userId,
        (typing) => {
          try {
            setIsTyping(typing);
          } catch (error) {
            console.error('Error processing typing update:', error);
          }
        }
      );

      return () => {
        try {
          unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing from typing:', error);
        }
      };
    } catch (error) {
      console.error('Error setting up typing subscription:', error);
    }
  }, [currentUser, otherUser]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    const messageToSend = newMessage;

    // Clear input immediately for better UX
    setNewMessage('');

    try {
      await sendMessage(
        currentUser.uid,
        currentUser.displayName,
        otherUser.userId,
        otherUser.displayName,
        messageToSend,
        roomCode,
        currentUser.photoURL,
        otherUser.photoURL
      );

      // Clear typing indicator (don't await to avoid blocking)
      setTypingStatus(currentUser.uid, otherUser.userId, false).catch(err => {
        console.warn('Could not clear typing status:', err);
      });
    } catch (error) {
      console.error('Error sending message:', error);
      // Restore message on error
      setNewMessage(messageToSend);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
      // Focus back on input after everything completes
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);

    // Update typing indicator
    if (e.target.value.trim()) {
      setTypingStatus(currentUser.uid, otherUser.userId, true);

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set new timeout to clear typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        setTypingStatus(currentUser.uid, otherUser.userId, false);
      }, 2000);
    } else {
      setTypingStatus(currentUser.uid, otherUser.userId, false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  if (!currentUser || !otherUser) {
    return null;
  }

  return (
    <div className={`chat-window ${isMinimized ? 'minimized' : ''} ${isFloating ? 'floating' : ''}`}>
      {/* Chat Header */}
      <div className="chat-header">
        <div className="chat-header-user">
          {otherUser?.photoURL && (
            <img
              src={safeString(otherUser.photoURL, '')}
              alt={safeString(otherUser?.displayName, 'User')}
              className="chat-user-avatar"
            />
          )}
          <div className="chat-user-info">
            <h4>{safeString(otherUser?.displayName, 'User')}</h4>
            {isTyping && <span className="typing-indicator">typing...</span>}
          </div>
        </div>

        <div className="chat-header-actions">
          {onToggleMinimize && (
            <button
              className="chat-minimize-btn"
              onClick={onToggleMinimize}
              title={isMinimized ? 'Expand' : 'Minimize'}
            >
              {isMinimized ? '▲' : '▼'}
            </button>
          )}
          {onClose && (
            <button
              className="chat-close-btn"
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Chat Messages */}
      {!isMinimized && (
        <>
          <div className="chat-messages" ref={messagesContainerRef}>
            {messages.length === 0 ? (
              <div className="chat-empty-state">
                <p>No messages yet</p>
                <small>Send a message to start the conversation!</small>
              </div>
            ) : (
              messages
                .filter((message) => message && message.id) // Filter out invalid messages
                .map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isOwnMessage={message.from === currentUser.uid}
                  />
                ))
            )}
          </div>

          {/* Chat Input */}
          <form className="chat-input-form" onSubmit={handleSendMessage}>
            <input
              ref={inputRef}
              type="text"
              className="chat-input"
              placeholder="Type a message..."
              value={newMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={isSending}
              autoComplete="off"
            />

            <button
              type="submit"
              className="chat-send-btn"
              disabled={!newMessage.trim() || isSending}
            >
              {isSending ? '...' : '📤'}
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default ChatWindow;
